import { FC, useState, useMemo, useEffect } from 'react';
import {
  Grid,
  Button,
  Box,
  Typography,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Badge,
  Card,
  CardContent,
  Divider,
} from '@mui/material';
import PaginatedGrid from '../../../../components/paginatedGrid/PaginatedGrid';
import {
  ArrowBack,
  Science,
  MenuBook,
  Computer,
  Edit,
  FilterList,
  ExpandMore,
  Close,
} from '@mui/icons-material';
import {
  useAcademicsMaterialsActions,
  useAcademicsMaterialsContext,
  useAcademicsMaterialsState,
} from '../../contexts/academicsMaterials.context';
import {
  IAcademicsMaterials,
  MaterialCategory,
  MaterialStatus,
  MaterialCondition,
  MaterialCategory as EMaterialCategory,
} from '../../../../../apis/academics/academicsMaterials.interfaces';
import {
  GridColDef,
  GridRenderCellParams,
  GridValidRowModel,
  GridValueFormatter,
} from '@mui/x-data-grid';
import { CURRENCY_SYMBOL } from '../../../../../constants/currency.constants';
import { useTranslation } from 'src/hooks/useTranslationHook';
import { TTranslateTextType } from 'src/hooks/useTranslationHook';
import { AddButton, EditButton } from 'src/views/components/simple/AllButtons';
import { useAcademicsService } from 'src/apis/academics/useAcademicsService';
import { useQueryClient } from '@tanstack/react-query';
import { getAcademicsMaterialsKey } from '../../hooks/useAcademicsMaterialsHook';
import { Delete } from '@mui/icons-material';
import { CircularProgress } from '@mui/material';

enum EMaterialView {
  MATERIALS = 'materials',
  DETAILS = 'details',
  INDIVIDUAL_DETAILS = 'individual_details',
}

interface AcademicsMaterialsGridProps {
  isLoading?: boolean;
}

interface MaterialRow extends GridValidRowModel {
  id: string;
  name: string;
  category: MaterialCategory;
  status: MaterialStatus;
  condition: MaterialCondition;
  quantity: number;
  location: string;
  cost: number;
  department: string;
  totalCount: number;
  availableCount: number;
  totalValue: number;
  totalCosts: number;
  assignedTo: string;
}

interface MaterialDetailRow extends GridValidRowModel {
  name: string;
  type: string;
  status: MaterialStatus;
  quantity: number;
  cost: number;
  supplier?: string;
  purchaseDate: Date;
}

interface FilterState {
  departments: Set<string>;
  statuses: Set<MaterialStatus>;
  conditions: Set<MaterialCondition>;
  categories: Set<MaterialCategory>;
}

const getMaterialIcon = (category: MaterialCategory) => {
  switch (category) {
    case EMaterialCategory.BOOK:
      return <MenuBook fontSize="small" />;
    case EMaterialCategory.LAB_EQUIPMENT:
      return <Science fontSize="small" />;
    case EMaterialCategory.DIGITAL_RESOURCE:
      return <Computer fontSize="small" />;
    default:
      return <Edit fontSize="small" />;
  }
};

const getMaterialsGridColumns = (
  translateText: TTranslateTextType
): GridColDef<MaterialRow>[] => [
    {
      field: 'name',
      headerName: translateText('academics.materials.columns.name'),
      flex: 1,
      minWidth: 200,
    },
    {
      field: 'category',
      headerName: translateText('academics.materials.columns.category'),
      width: 150,
      renderCell: (params: GridRenderCellParams<MaterialRow>) => {
        const category = params.value as MaterialCategory;
        return (
          <Box
            sx={{ display: 'flex', alignItems: 'center', gap: 1, height: '100%' }}
            data-testid="material-category-cell"
          >
            {category && getMaterialIcon(category)}
            <Typography variant="body2">{category}</Typography>
          </Box>
        );
      },
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 120,
      renderCell: (params: GridRenderCellParams<MaterialRow>) => {
        const status = params.value as MaterialStatus;
        return (
          <Chip
            label={status}
            color={
              status === MaterialStatus.AVAILABLE
                ? 'success'
                : status === MaterialStatus.IN_USE
                  ? 'info'
                  : status === MaterialStatus.MAINTENANCE
                    ? 'warning'
                    : status === MaterialStatus.RETIRED
                      ? 'default'
                      : 'error'
            }
            size="small"
          />
        );
      },
    },
    {
      field: 'condition',
      headerName: translateText('academics.materials.columns.condition'),
      width: 120,
      renderCell: (params: GridRenderCellParams<MaterialRow>) => {
        const condition = params.value as MaterialCondition;
        return (
          <Chip
            label={condition}
            color={
              condition === MaterialCondition.NEW
                ? 'success'
                : condition === MaterialCondition.LIKE_NEW
                  ? 'info'
                  : condition === MaterialCondition.GOOD
                    ? 'primary'
                    : condition === MaterialCondition.FAIR
                      ? 'warning'
                      : 'error'
            }
            size="small"
          />
        );
      },
    },
    {
      field: 'quantity',
      headerName: translateText('academics.materials.columns.quantity'),
      width: 100,
      type: 'number',
    },
    {
      field: 'totalCosts',
      headerName: translateText('academics.materials.columns.totalCosts'),
      width: 150,
      type: 'number',
      renderCell: (params: GridRenderCellParams<MaterialRow>) => {
        const totalCosts = params.value as number;
        return (
          <Typography
            variant="body2"
            sx={{
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'end',
            }}
          >
            {`${CURRENCY_SYMBOL}${totalCosts.toFixed(2)}`}
          </Typography>
        );
      },
    },
    {
      field: 'assignedTo',
      headerName: translateText('academics.materials.assignedTo'),
      width: 180,
      renderCell: (params: GridRenderCellParams<MaterialRow>) => (
        <Typography variant="body2" data-testid="material-assigned-to-cell">
          {params.value || '-'}
        </Typography>
      ),
    },
  ];

const getMaterialDetailsGridColumns = (
  translateText: TTranslateTextType
): GridColDef<MaterialDetailRow>[] => [
    {
      field: 'name',
      headerName: translateText('academics.materials.columns.name'),
      flex: 1,
      minWidth: 200,
    },
    {
      field: 'type',
      headerName: translateText('academics.materials.columns.type'),
      width: 150,
    },
    {
      field: 'status',
      headerName: translateText('academics.materials.columns.status'),
      width: 120,
      renderCell: (params: GridRenderCellParams<MaterialDetailRow>) => {
        const status = params.value as MaterialStatus;
        return (
          <Chip
            label={status}
            color={
              status === MaterialStatus.AVAILABLE
                ? 'success'
                : status === MaterialStatus.IN_USE
                  ? 'info'
                  : status === MaterialStatus.MAINTENANCE
                    ? 'warning'
                    : status === MaterialStatus.RETIRED
                      ? 'default'
                      : 'error'
            }
            size="small"
          />
        );
      },
    },
    {
      field: 'quantity',
      headerName: translateText('academics.materials.columns.quantity'),
      width: 100,
      type: 'number',
    },
    {
      field: 'cost',
      headerName: translateText('academics.materials.columns.cost'),
      width: 120,
      type: 'number',
      valueFormatter: ((params: any) => {
        if (!params) {
          console.warn('Cost formatter received undefined params');
          return '';
        }
        const value = Number(params.value);
        if (isNaN(value)) {
          console.warn('Cost formatter received invalid value:', params.value);
          return '';
        }
        return `${CURRENCY_SYMBOL}${value.toFixed(2)}`;
      }) as GridValueFormatter<MaterialDetailRow>,
    },
    {
      field: 'supplier',
      headerName: translateText('academics.materials.columns.supplier'),
      width: 150,
    },
    {
      field: 'purchaseDate',
      headerName: translateText('academics.materials.columns.purchaseDate'),
      width: 150,
      type: 'date',
      valueFormatter: ((params: any) => {
        if (!params) {
          console.warn('Purchase date formatter received undefined params');
          return '';
        }
        try {
          const value = new Date(params.value);
          if (isNaN(value.getTime())) {
            console.warn(
              'Purchase date formatter received invalid date:',
              params.value
            );
            return '';
          }
          return value.toLocaleDateString();
        } catch (error) {
          console.warn('Purchase date formatter error:', error);
          return '';
        }
      }) as GridValueFormatter<MaterialDetailRow>,
    },
  ];

const AcademicsMaterialsGrid: FC<AcademicsMaterialsGridProps> = ({
  isLoading: propIsLoading,
}) => {
  const { translateText } = useTranslation();
  const {
    materialsQuery,
    getTotalInventoryValue,
    getDepartmentInventorySummary,
    getCategoryInventorySummary,
    // getStatusInventorySummary,
  } = useAcademicsMaterialsContext();

  const { academicsMaterialsAppState } = useAcademicsMaterialsState();

  const {
    setIsManageMaterialOpen,
    setIsCreateMode,
    setIsEditMode,
    setSelectedMaterial,
    setSelectedMaterialId,
    setSelectedEachMaterial,
    setSelectedEachMaterialId,
    setSelectedMaterialGroupId,
    setIsCreateEachMaterialMode,
    setIsEditEachMaterialMode,
    setIsManageEachMaterialOpen,
  } = useAcademicsMaterialsActions();

  const [viewType, setViewType] = useState<EMaterialView>(
    EMaterialView.MATERIALS
  );
  const [materials, setMaterials] = useState<IAcademicsMaterials[]>([]);
  const [filters, setFilters] = useState<FilterState>({
    departments: new Set(),
    statuses: new Set(),
    conditions: new Set(),
    categories: new Set(),
  });

  const materialsGridColumns = useMemo(
    () => getMaterialsGridColumns(translateText),
    [translateText]
  );
  const materialDetailsGridColumns = useMemo(
    () => getMaterialDetailsGridColumns(translateText),
    [translateText]
  );

  const isLoading = propIsLoading || materialsQuery.isLoading;

  useEffect(() => {
    if (materialsQuery.data) {
      const validMaterials = materialsQuery.data.materials.filter(
        (material) => {
          const isValid =
            material && material.id && Array.isArray(material.materials);
          if (!isValid) {
            console.warn('Invalid material data:', material);
          }
          return isValid;
        }
      );
      // Only update if the data actually changed to prevent duplicates
      setMaterials((prev) => {
        const prevIds = new Set(prev.map((m) => m.id));
        const newIds = new Set(validMaterials.map((m) => m.id));
        if (
          prev.length === validMaterials.length &&
          prevIds.size === newIds.size &&
          Array.from(prevIds).every((id) => newIds.has(id))
        ) {
          return prev;
        }
        return validMaterials;
      });
    } else {
      setMaterials([]);
    }
  }, [materialsQuery.data]);

  // Sync viewType with context state
  useEffect(() => {
    if (academicsMaterialsAppState.selectedEachMaterial) {
      setViewType(EMaterialView.INDIVIDUAL_DETAILS);
    } else if (academicsMaterialsAppState.selectedMaterial) {
      setViewType(EMaterialView.DETAILS);
    } else if (!academicsMaterialsAppState.selectedMaterialId) {
      setViewType(EMaterialView.MATERIALS);
    }
  }, [
    academicsMaterialsAppState.selectedMaterial,
    academicsMaterialsAppState.selectedMaterialId,
    academicsMaterialsAppState.selectedEachMaterial,
  ]);

  // Transform materials into rows for the grid - show groups with name field
  const materialRows = useMemo(() => {
    return materials.map((materialGroup) => {
      // Get the most common category, status, and condition from materials in the group
      const categoryCounts = new Map<MaterialCategory, number>();
      const statusCounts = new Map<MaterialStatus, number>();
      const conditionCounts = new Map<MaterialCondition, number>();

      materialGroup.materials.forEach((material) => {
        categoryCounts.set(
          material.category,
          (categoryCounts.get(material.category) || 0) + 1
        );
        statusCounts.set(
          material.status,
          (statusCounts.get(material.status) || 0) + 1
        );
        conditionCounts.set(
          material.condition,
          (conditionCounts.get(material.condition) || 0) + 1
        );
      });

      const mostCommonCategory =
        Array.from(categoryCounts.entries()).sort(
          (a, b) => b[1] - a[1]
        )[0]?.[0] || MaterialCategory.OTHER;
      const mostCommonStatus =
        Array.from(statusCounts.entries()).sort(
          (a, b) => b[1] - a[1]
        )[0]?.[0] || MaterialStatus.AVAILABLE;
      const mostCommonCondition =
        Array.from(conditionCounts.entries()).sort(
          (a, b) => b[1] - a[1]
        )[0]?.[0] || MaterialCondition.GOOD;

      // Calculate total costs as sum of all materials' costs (cost * quantity)
      const totalCosts = materialGroup.materials.reduce(
        (sum, material) => sum + material.cost * material.quantity,
        0
      );

      const assignedToParts: string[] = [];
      if (materialGroup.assignedTo?.studentIds?.length) {
        assignedToParts.push(`${materialGroup.assignedTo.studentIds.length} student(s)`);
      }
      if (materialGroup.assignedTo?.classIds?.length) {
        assignedToParts.push(`${materialGroup.assignedTo.classIds.length} class(es)`);
      }
      const assignedTo = assignedToParts.length ? assignedToParts.join(', ') : undefined;

      return {
        id: materialGroup.id,
        name: materialGroup.name,
        category: mostCommonCategory,
        status: mostCommonStatus,
        condition: mostCommonCondition,
        quantity: materialGroup.totalCount,
        location: materialGroup.location,
        cost: materialGroup.totalValue,
        department: materialGroup.department,
        totalCount: materialGroup.totalCount,
        availableCount: materialGroup.availableCount,
        totalValue: materialGroup.totalValue,
        totalCosts,
        assignedTo,
      };
    });
  }, [materials]);

  // Filter materials based on all active filters
  const filteredMaterials = useMemo(() => {
    return materialRows.filter((row) => {
      // Department filter
      if (
        filters.departments.size > 0 &&
        !filters.departments.has(row.department)
      ) {
        return false;
      }

      // Status filter
      if (filters.statuses.size > 0 && !filters.statuses.has(row.status)) {
        return false;
      }

      // Condition filter
      if (
        filters.conditions.size > 0 &&
        !filters.conditions.has(row.condition)
      ) {
        return false;
      }

      // Category filter
      if (
        filters.categories.size > 0 &&
        !filters.categories.has(row.category)
      ) {
        return false;
      }

      return true;
    });
  }, [materialRows, filters]);

  const toggleFilter = (
    filterType: keyof FilterState,
    value: string | MaterialStatus | MaterialCondition | MaterialCategory
  ) => {
    setFilters((prev) => {
      const newFilters = { ...prev };
      const currentSet = new Set(prev[filterType]);

      if (currentSet.has(value)) {
        currentSet.delete(value);
      } else {
        currentSet.add(value);
      }

      return {
        ...newFilters,
        [filterType]: currentSet,
      };
    });
  };

  const clearFilters = (filterType?: keyof FilterState) => {
    if (filterType) {
      setFilters((prev) => ({
        ...prev,
        [filterType]: new Set(),
      }));
    } else {
      setFilters({
        departments: new Set(),
        statuses: new Set(),
        conditions: new Set(),
        categories: new Set(),
      });
    }
  };

  const hasActiveFilters = () => {
    return Object.values(filters).some((set) => set.size > 0);
  };

  const getActiveFilterCount = (filterType: keyof FilterState) => {
    return filters[filterType].size;
  };

  const handleAddMaterialGroup = () => {
    // Handle add material logic
    setIsManageMaterialOpen(true);
    setIsCreateMode(true);
    setIsEditMode(false);
    setIsManageEachMaterialOpen(false);
    setIsCreateEachMaterialMode(false);
    setIsEditEachMaterialMode(false);
  };

  const handleEditMaterialGroup = () => {
    setIsManageMaterialOpen(true);
    setIsCreateMode(false);
    setIsEditMode(true);
    setIsManageEachMaterialOpen(false);
    setIsCreateEachMaterialMode(false);
    setIsEditEachMaterialMode(false);
  };

  const handleAddNewMaterialToGroup = () => {
    setIsManageEachMaterialOpen(true);
    setIsCreateEachMaterialMode(true);
    setIsEditEachMaterialMode(false);
    setIsManageMaterialOpen(false);
    setIsCreateMode(false);
    setIsEditMode(false);
  };

  const handleEditNewMaterialToGroup = () => {
    setIsManageEachMaterialOpen(true);
    setIsCreateEachMaterialMode(false);
    setIsEditEachMaterialMode(true);
    setIsManageMaterialOpen(false);
    setIsCreateMode(false);
    setIsEditMode(false);
  };

  const { deleteAcademicsMaterialItem, deleteAcademicsMaterialGroup } = useAcademicsService();
  const queryClient = useQueryClient();
  const [isDeletingItem, setIsDeletingItem] = useState(false);
  const [isDeletingGroup, setIsDeletingGroup] = useState(false);

  const handleDeleteMaterialItem = async () => {
    const item = academicsMaterialsAppState.selectedEachMaterial;
    const group = academicsMaterialsAppState.selectedMaterial;
    if (!item || !group) return;
    setIsDeletingItem(true);
    try {
      await deleteAcademicsMaterialItem(group.id, item.id);
      queryClient.invalidateQueries({ queryKey: [getAcademicsMaterialsKey] });
      handleBackToGroupDetails();
    } finally {
      setIsDeletingItem(false);
    }
  };

  const handleDeleteMaterialGroup = async () => {
    const group = academicsMaterialsAppState.selectedMaterial;
    if (!group) return;
    setIsDeletingGroup(true);
    try {
      await deleteAcademicsMaterialGroup(group.id);
      queryClient.invalidateQueries({ queryKey: [getAcademicsMaterialsKey] });
      handleBackToMaterials();
    } finally {
      setIsDeletingGroup(false);
    }
  };

  const handleRowClick = (params: any) => {
    if (viewType === EMaterialView.MATERIALS) {
      // Find the material group by id
      const material = materials.find((m) => m.id === params.row.id);
      if (material) {
        setSelectedMaterial(material);
        setSelectedMaterialId(material.id);
        setViewType(EMaterialView.DETAILS);
      }
    } else if (viewType === EMaterialView.DETAILS) {
      // Find the individual material by id from the selected material group
      const materialGroup = academicsMaterialsAppState.selectedMaterial;
      if (materialGroup) {
        const individualMaterial = materialGroup.materials.find(
          (m) => m.id === params.row.id
        );
        if (individualMaterial) {
          setSelectedEachMaterial(individualMaterial);
          setSelectedEachMaterialId(individualMaterial.id);
          setSelectedMaterialGroupId(materialGroup.id);
          setViewType(EMaterialView.INDIVIDUAL_DETAILS);
        }
      }
    }
  };

  const handleBackToMaterials = () => {
    setViewType(EMaterialView.MATERIALS);
    setSelectedMaterial(null);
    setSelectedMaterialId(null);
    setSelectedEachMaterial(null);
    setSelectedEachMaterialId(null);
    setSelectedMaterialGroupId(null);
  };

  const handleBackToGroupDetails = () => {
    setViewType(EMaterialView.DETAILS);
    setSelectedEachMaterial(null);
    setSelectedEachMaterialId(null);
    setSelectedMaterialGroupId(null);
  };

  const totalValue = getTotalInventoryValue();
  const departmentSummary = getDepartmentInventorySummary();
  const categorySummary = getCategoryInventorySummary();
  // const statusSummary = getStatusInventorySummary();

  return (
    <Grid
      container
      spacing={0}
      sx={{ height: '100%' }}
      data-testid="academics-materials-grid"
    >
      <Grid
        item
        xs={12}
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          alignItems: { xs: 'flex-start', sm: 'center' },
          justifyContent: 'space-between',
          gap: { xs: 2, sm: 0 },
          mb: { xs: 2, sm: 2 },
        }}
        data-testid="academics-materials-header"
      >
        {viewType === EMaterialView.INDIVIDUAL_DETAILS &&
          academicsMaterialsAppState.selectedEachMaterial ? (
          <Box
            display="flex"
            alignItems="center"
            data-testid="individual-material-detail-header"
          >
            <Button
              startIcon={<ArrowBack />}
              onClick={handleBackToGroupDetails}
              sx={{ mr: 2 }}
              data-testid="back-to-group-details-button"
            >
              {translateText('academics.materials.backToGroupDetails')}
            </Button>
            <Typography
              variant="h6"
              data-testid="individual-detail-header-title"
            >
              {academicsMaterialsAppState.selectedEachMaterial.name}
            </Typography>
          </Box>
        ) : viewType === EMaterialView.DETAILS &&
          academicsMaterialsAppState.selectedMaterial ? (
          <Box
            display="flex"
            alignItems="center"
            data-testid="materials-detail-header"
          >
            <Button
              startIcon={<ArrowBack />}
              onClick={handleBackToMaterials}
              sx={{ mr: 2 }}
              data-testid="back-to-materials-button"
            >
              {translateText('academics.materials.backToMaterials')}
            </Button>
            <Typography variant="h6" data-testid="detail-header-title">
              {academicsMaterialsAppState.selectedMaterial.department} -{' '}
              {translateText('academics.materials.materials')}
            </Typography>
          </Box>
        ) : (
          <Box data-testid="materials-primary-header">
            <Typography
              variant="body2"
              color="textSecondary"
              data-testid="total-value-text"
            >
              {translateText('academics.materials.totalValue')}:{' '}
              {totalValue.toFixed(2)} {CURRENCY_SYMBOL}
            </Typography>
          </Box>
        )}

        <Box
          sx={{
            width: { xs: '100%', sm: 'auto' },
            display: 'flex',
            justifyContent: { xs: 'flex-start', sm: 'flex-end' },
          }}
        >
          {viewType === EMaterialView.MATERIALS ? (
            <AddButton
              label={translateText('academics.materials.addMaterial')}
              onClickHandler={handleAddMaterialGroup}
            />
          ) : viewType === EMaterialView.DETAILS ? (
            <Box sx={{ display: 'flex', gap: 1 }}>
              <EditButton
                label={translateText('academics.materials.editMaterial')}
                onClickHandler={handleEditMaterialGroup}
              />
              <Button
                variant="outlined"
                color="error"
                startIcon={isDeletingGroup ? <CircularProgress size={16} /> : <Delete />}
                onClick={handleDeleteMaterialGroup}
                disabled={isDeletingGroup}
                sx={{ minHeight: { xs: '44px', sm: '36px' } }}
                data-testid="delete-material-group-button"
              >
                Delete
              </Button>
            </Box>
          ) : viewType === EMaterialView.INDIVIDUAL_DETAILS ? (
            <Box sx={{ display: 'flex', gap: 1 }}>
              <EditButton
                label={translateText('academics.materials.editItem')}
                onClickHandler={handleEditNewMaterialToGroup}
              />
              <Button
                variant="outlined"
                color="error"
                startIcon={isDeletingItem ? <CircularProgress size={16} /> : <Delete />}
                onClick={handleDeleteMaterialItem}
                disabled={isDeletingItem}
                sx={{ minHeight: { xs: '44px', sm: '36px' } }}
                data-testid="delete-material-item-button"
              >
                Delete
              </Button>
            </Box>
          ) : null}
        </Box>
      </Grid>

      {viewType === EMaterialView.MATERIALS && (
        <Grid
          item
          xs={12}
          sx={{ mb: 2 }}
          data-testid="materials-filters-container"
        >
          <Accordion
            defaultExpanded={hasActiveFilters()}
            sx={{
              '&:before': {
                display: 'none',
              },
              boxShadow: 'none',
              border: '1px solid',
              borderColor: 'divider',
            }}
            data-testid="materials-filters-accordion"
          >
            <AccordionSummary
              expandIcon={<ExpandMore />}
              sx={{
                '& .MuiAccordionSummary-content': {
                  margin: '8px 0',
                },
              }}
              data-testid="filters-accordion-summary"
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  width: '100%',
                }}
              >
                <FilterList color="action" />
                <Typography variant="subtitle1" sx={{ flexGrow: 1 }}>
                  {translateText('academics.materials.filters')}
                </Typography>
                {hasActiveFilters() && (
                  <>
                    <Badge
                      badgeContent={Object.values(filters).reduce(
                        (acc, set) => acc + set.size,
                        0
                      )}
                      color="primary"
                      sx={{ ml: 1 }}
                    >
                      <Chip
                        size="medium"
                        label={translateText(
                          'academics.materials.activeFilters'
                        )}
                        color="primary"
                        variant="outlined"
                      />
                    </Badge>
                    <Button
                      variant="outlined"
                      color="error"
                      size="small"
                      startIcon={<Close />}
                      onClick={(e) => {
                        e.stopPropagation();
                        clearFilters();
                      }}
                      sx={{ ml: 2 }}
                      data-testid="clear-all-filters-button"
                    >
                      {translateText('academics.materials.clearAllFilters')}
                    </Button>
                  </>
                )}
              </Box>
            </AccordionSummary>
            <AccordionDetails sx={{ pt: 0 }}>
              <Grid container spacing={{ xs: 1, sm: 2 }}>
                <Grid item xs={12} data-testid="departments-filter-section">
                  <Box sx={{ mb: 2 }}>
                    <Typography
                      variant="subtitle2"
                      color="textSecondary"
                      gutterBottom
                    >
                      {translateText('academics.materials.departments')}
                    </Typography>
                    <Box
                      sx={{
                        display: 'flex',
                        gap: { xs: 0.5, sm: 1 },
                        flexWrap: 'wrap',
                      }}
                      data-testid="departments-filter-chips"
                    >
                      {departmentSummary.map((dept) => (
                        <Chip
                          key={dept.department}
                          label={`${dept.department} (${dept.totalCount})`}
                          color={
                            filters.departments.has(dept.department)
                              ? 'primary'
                              : 'default'
                          }
                          variant={
                            filters.departments.has(dept.department)
                              ? 'filled'
                              : 'outlined'
                          }
                          onClick={() =>
                            toggleFilter('departments', dept.department)
                          }
                          sx={{
                            cursor: 'pointer',
                            fontSize: { xs: '0.75rem', sm: '0.875rem' },
                            minHeight: { xs: '32px', sm: '32px' },
                            '&:hover': {
                              backgroundColor: filters.departments.has(
                                dept.department
                              )
                                ? 'primary.main'
                                : 'action.hover',
                            },
                          }}
                          data-testid={`department-filter-chip-${dept.department}`}
                        />
                      ))}
                      {getActiveFilterCount('departments') > 0 && (
                        <Chip
                          label={translateText(
                            'academics.materials.clearDepartments'
                          )}
                          color="error"
                          variant="outlined"
                          onClick={() => clearFilters('departments')}
                          sx={{ cursor: 'pointer' }}
                          data-testid="clear-departments-filter-button"
                        />
                      )}
                    </Box>
                  </Box>
                </Grid>

                <Grid item xs={12} data-testid="categories-filter-section">
                  <Box sx={{ mb: 2 }}>
                    <Typography
                      variant="subtitle2"
                      color="textSecondary"
                      gutterBottom
                    >
                      {translateText('academics.materials.categories')}
                    </Typography>
                    <Box
                      sx={{
                        display: 'flex',
                        gap: 1,
                        flexWrap: 'wrap',
                        justifyContent: 'center',
                      }}
                      data-testid="categories-filter-chips"
                    >
                      {categorySummary.map((cat) => (
                        <Chip
                          key={cat.category}
                          label={`${cat.category} (${cat.totalCount})`}
                          color={
                            filters.categories.has(cat.category)
                              ? 'primary'
                              : 'default'
                          }
                          variant={
                            filters.categories.has(cat.category)
                              ? 'filled'
                              : 'outlined'
                          }
                          onClick={() =>
                            toggleFilter('categories', cat.category)
                          }
                          sx={{
                            cursor: 'pointer',
                            '&:hover': {
                              backgroundColor: filters.categories.has(
                                cat.category
                              )
                                ? 'primary.main'
                                : 'action.hover',
                            },
                          }}
                          data-testid={`category-filter-chip-${cat.category}`}
                        />
                      ))}
                      {getActiveFilterCount('categories') > 0 && (
                        <Chip
                          label={translateText(
                            'academics.materials.clearCategories'
                          )}
                          color="error"
                          variant="outlined"
                          onClick={() => clearFilters('categories')}
                          sx={{ cursor: 'pointer' }}
                          data-testid="clear-categories-filter-button"
                        />
                      )}
                    </Box>
                  </Box>
                </Grid>

                <Grid item xs={12} data-testid="status-filter-section">
                  <Box sx={{ mb: 2 }}>
                    <Typography
                      variant="subtitle2"
                      color="textSecondary"
                      gutterBottom
                    >
                      {translateText('academics.materials.status')}
                    </Typography>
                    <Box
                      sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}
                      data-testid="status-filter-chips"
                    >
                      {Object.values(MaterialStatus).map((status) => (
                        <Chip
                          key={status}
                          label={status}
                          color={
                            filters.statuses.has(status) ? 'primary' : 'default'
                          }
                          variant={
                            filters.statuses.has(status) ? 'filled' : 'outlined'
                          }
                          onClick={() => toggleFilter('statuses', status)}
                          sx={{
                            cursor: 'pointer',
                            '&:hover': {
                              backgroundColor: filters.statuses.has(status)
                                ? 'primary.main'
                                : 'action.hover',
                            },
                          }}
                          data-testid={`status-filter-chip-${status}`}
                        />
                      ))}
                      {getActiveFilterCount('statuses') > 0 && (
                        <Chip
                          label={translateText(
                            'academics.materials.clearStatus'
                          )}
                          color="error"
                          variant="outlined"
                          onClick={() => clearFilters('statuses')}
                          sx={{ cursor: 'pointer' }}
                          data-testid="clear-status-filter-button"
                        />
                      )}
                    </Box>
                  </Box>
                </Grid>

                <Grid item xs={12} data-testid="condition-filter-section">
                  <Box sx={{ mb: 2 }}>
                    <Typography
                      variant="subtitle2"
                      color="textSecondary"
                      gutterBottom
                    >
                      {translateText('academics.materials.condition')}
                    </Typography>
                    <Box
                      sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}
                      data-testid="condition-filter-chips"
                    >
                      {Object.values(MaterialCondition).map((condition) => (
                        <Chip
                          key={condition}
                          label={condition}
                          color={
                            filters.conditions.has(condition)
                              ? 'primary'
                              : 'default'
                          }
                          variant={
                            filters.conditions.has(condition)
                              ? 'filled'
                              : 'outlined'
                          }
                          onClick={() => toggleFilter('conditions', condition)}
                          sx={{
                            cursor: 'pointer',
                            '&:hover': {
                              backgroundColor: filters.conditions.has(condition)
                                ? 'primary.main'
                                : 'action.hover',
                            },
                          }}
                          data-testid={`condition-filter-chip-${condition}`}
                        />
                      ))}
                      {getActiveFilterCount('conditions') > 0 && (
                        <Chip
                          label={translateText(
                            'academics.materials.clearCondition'
                          )}
                          color="error"
                          variant="outlined"
                          onClick={() => clearFilters('conditions')}
                          sx={{ cursor: 'pointer' }}
                          data-testid="clear-condition-filter-button"
                        />
                      )}
                    </Box>
                  </Box>
                </Grid>

                {hasActiveFilters() && (
                  <Grid item xs={12} data-testid="clear-all-filters-section">
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                      <Chip
                        label={translateText(
                          'academics.materials.clearAllFilters'
                        )}
                        color="error"
                        variant="outlined"
                        onClick={() => clearFilters()}
                        sx={{ cursor: 'pointer' }}
                        data-testid="clear-all-filters-chip"
                      />
                    </Box>
                  </Grid>
                )}
              </Grid>
            </AccordionDetails>
          </Accordion>
        </Grid>
      )}

      {viewType === EMaterialView.DETAILS &&
        academicsMaterialsAppState.selectedMaterial && (
          <>
            <Grid item xs={12} data-testid="materials-detail-info-container">
              <Box sx={{ mb: 2 }}>
                <Typography
                  variant="body2"
                  color="textSecondary"
                  data-testid="detail-info-text"
                >
                  {translateText('academics.materials.showingItems', {
                    count:
                      academicsMaterialsAppState.selectedMaterial.materials
                        .length,
                    department:
                      academicsMaterialsAppState.selectedMaterial.department,
                  })}
                </Typography>
              </Box>
            </Grid>

            <Grid
              item
              xs={12}
              sx={{ mb: 2 }}
              data-testid="materials-group-metadata-container"
            >
              <Card variant="outlined">
                <CardContent sx={{ p: { xs: 1, sm: 2 }, '&:last-child': { pb: { xs: 1, sm: 2 } } }}>
                  <Typography
                    variant="h6"
                    fontWeight="bold"
                    sx={{
                      mb: 1.5,
                      fontSize: { xs: '1.25rem', sm: '1.5rem' },
                    }}
                    data-testid="metadata-title"
                  >
                    {translateText('academics.materials.groupMetadata')}
                  </Typography>
                  <Divider sx={{ mb: 1.5 }} />
                  <Grid container spacing={{ xs: 1, sm: 2 }}>
                    <Grid
                      item
                      xs={12}
                      sm={6}
                      md={3}
                      data-testid="metadata-name"
                    >
                      <Typography variant="caption" color="textSecondary">
                        {translateText('academics.materials.name')}
                      </Typography>
                      <Typography variant="body1" fontWeight="medium">
                        {academicsMaterialsAppState.selectedMaterial.name ||
                          translateText('academics.materials.nA')}
                      </Typography>
                    </Grid>
                    <Grid
                      item
                      xs={12}
                      sm={6}
                      md={3}
                      data-testid="metadata-department"
                    >
                      <Typography variant="caption" color="textSecondary">
                        {translateText('academics.materials.department')}
                      </Typography>
                      <Typography variant="body1" fontWeight="medium">
                        {academicsMaterialsAppState.selectedMaterial
                          .department ||
                          translateText('academics.materials.nA')}
                      </Typography>
                    </Grid>
                    <Grid
                      item
                      xs={12}
                      sm={6}
                      md={3}
                      data-testid="metadata-location"
                    >
                      <Typography variant="caption" color="textSecondary">
                        {translateText('academics.materials.columns.location')}
                      </Typography>
                      <Typography variant="body1" fontWeight="medium">
                        {academicsMaterialsAppState.selectedMaterial.location ||
                          translateText('academics.materials.nA')}
                      </Typography>
                    </Grid>
                    <Grid
                      item
                      xs={12}
                      sm={6}
                      md={3}
                      data-testid="metadata-category"
                    >
                      <Typography variant="caption" color="textSecondary">
                        {translateText('academics.materials.columns.category')}
                      </Typography>
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 0.5,
                          mt: 0.5,
                        }}
                      >
                        {getMaterialIcon(
                          academicsMaterialsAppState.selectedMaterial.category
                        )}
                        <Typography variant="body1" fontWeight="medium">
                          {academicsMaterialsAppState.selectedMaterial
                            .category ||
                            translateText('academics.materials.nA')}
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid
                      item
                      xs={12}
                      sm={6}
                      md={3}
                      data-testid="metadata-status"
                    >
                      <Typography variant="caption" color="textSecondary">
                        {translateText('academics.materials.columns.status')}
                      </Typography>
                      <Box sx={{ mt: 0.5 }}>
                        <Chip
                          label={
                            academicsMaterialsAppState.selectedMaterial.status
                          }
                          color={
                            academicsMaterialsAppState.selectedMaterial
                              .status === MaterialStatus.AVAILABLE
                              ? 'success'
                              : academicsMaterialsAppState.selectedMaterial
                                .status === MaterialStatus.IN_USE
                                ? 'info'
                                : academicsMaterialsAppState.selectedMaterial
                                  .status === MaterialStatus.MAINTENANCE
                                  ? 'warning'
                                  : academicsMaterialsAppState.selectedMaterial
                                    .status === MaterialStatus.RETIRED
                                    ? 'default'
                                    : 'error'
                          }
                          size="small"
                        />
                      </Box>
                    </Grid>
                    <Grid
                      item
                      xs={12}
                      sm={6}
                      md={3}
                      data-testid="metadata-condition"
                    >
                      <Typography variant="caption" color="textSecondary">
                        {translateText('academics.materials.columns.condition')}
                      </Typography>
                      <Box sx={{ mt: 0.5 }}>
                        <Chip
                          label={
                            academicsMaterialsAppState.selectedMaterial
                              .condition
                          }
                          color={
                            academicsMaterialsAppState.selectedMaterial
                              .condition === MaterialCondition.NEW
                              ? 'success'
                              : academicsMaterialsAppState.selectedMaterial
                                .condition === MaterialCondition.LIKE_NEW
                                ? 'info'
                                : academicsMaterialsAppState.selectedMaterial
                                  .condition === MaterialCondition.GOOD
                                  ? 'primary'
                                  : academicsMaterialsAppState.selectedMaterial
                                    .condition === MaterialCondition.FAIR
                                    ? 'warning'
                                    : 'error'
                          }
                          size="small"
                        />
                      </Box>
                    </Grid>
                    <Grid
                      item
                      xs={12}
                      sm={6}
                      md={3}
                      data-testid="metadata-purchase-date"
                    >
                      <Typography variant="caption" color="textSecondary">
                        {translateText('academics.materials.purchaseDate')}
                      </Typography>
                      <Typography variant="body1" fontWeight="medium">
                        {academicsMaterialsAppState.selectedMaterial
                          .purchaseDate
                          ? new Date(
                            academicsMaterialsAppState.selectedMaterial.purchaseDate
                          ).toLocaleDateString()
                          : translateText('academics.materials.nA')}
                      </Typography>
                    </Grid>
                    <Grid
                      item
                      xs={12}
                      sm={6}
                      md={3}
                      data-testid="metadata-last-maintenance-date"
                    >
                      <Typography variant="caption" color="textSecondary">
                        {translateText(
                          'academics.materials.lastMaintenanceDate'
                        )}
                      </Typography>
                      <Typography variant="body1" fontWeight="medium">
                        {academicsMaterialsAppState.selectedMaterial
                          .lastMaintenanceDate
                          ? new Date(
                            academicsMaterialsAppState.selectedMaterial.lastMaintenanceDate
                          ).toLocaleDateString()
                          : translateText('academics.materials.nA')}
                      </Typography>
                    </Grid>
                    <Grid
                      item
                      xs={12}
                      sm={6}
                      md={3}
                      data-testid="metadata-last-inventory-date"
                    >
                      <Typography variant="caption" color="textSecondary">
                        {translateText('academics.materials.lastInventoryDate')}
                      </Typography>
                      <Typography variant="body1" fontWeight="medium">
                        {academicsMaterialsAppState.selectedMaterial
                          .lastInventoryDate
                          ? new Date(
                            academicsMaterialsAppState.selectedMaterial.lastInventoryDate
                          ).toLocaleDateString()
                          : translateText('academics.materials.nA')}
                      </Typography>
                    </Grid>
                    <Grid
                      item
                      xs={12}
                      sm={6}
                      md={3}
                      data-testid="metadata-total-count"
                    >
                      <Typography variant="caption" color="textSecondary">
                        {translateText('academics.materials.totalCount')}
                      </Typography>
                      <Typography variant="body1" fontWeight="medium">
                        {academicsMaterialsAppState.selectedMaterial
                          .totalCount ??
                          translateText('academics.materials.nA')}
                      </Typography>
                    </Grid>
                    <Grid
                      item
                      xs={12}
                      sm={6}
                      md={3}
                      data-testid="metadata-available-count"
                    >
                      <Typography variant="caption" color="textSecondary">
                        {translateText('academics.materials.availableCount')}
                      </Typography>
                      <Typography variant="body1" fontWeight="medium">
                        {academicsMaterialsAppState.selectedMaterial
                          .availableCount ??
                          translateText('academics.materials.nA')}
                      </Typography>
                    </Grid>
                    <Grid
                      item
                      xs={12}
                      sm={6}
                      md={3}
                      data-testid="metadata-in-use-count"
                    >
                      <Typography variant="caption" color="textSecondary">
                        {translateText('academics.materials.inUseCount')}
                      </Typography>
                      <Typography variant="body1" fontWeight="medium">
                        {academicsMaterialsAppState.selectedMaterial
                          .inUseCount ??
                          translateText('academics.materials.nA')}
                      </Typography>
                    </Grid>
                    <Grid
                      item
                      xs={12}
                      sm={6}
                      md={3}
                      data-testid="metadata-maintenance-count"
                    >
                      <Typography variant="caption" color="textSecondary">
                        {translateText('academics.materials.maintenanceCount')}
                      </Typography>
                      <Typography variant="body1" fontWeight="medium">
                        {academicsMaterialsAppState.selectedMaterial
                          .maintenanceCount ??
                          translateText('academics.materials.nA')}
                      </Typography>
                    </Grid>
                    <Grid
                      item
                      xs={12}
                      sm={6}
                      md={3}
                      data-testid="metadata-retired-count"
                    >
                      <Typography variant="caption" color="textSecondary">
                        {translateText('academics.materials.retiredCount')}
                      </Typography>
                      <Typography variant="body1" fontWeight="medium">
                        {academicsMaterialsAppState.selectedMaterial
                          .retiredCount ??
                          translateText('academics.materials.nA')}
                      </Typography>
                    </Grid>
                    <Grid
                      item
                      xs={12}
                      sm={6}
                      md={3}
                      data-testid="metadata-total-value"
                    >
                      <Typography variant="caption" color="textSecondary">
                        {translateText('academics.materials.totalValue')}
                      </Typography>
                      <Typography variant="body1" fontWeight="medium">
                        {academicsMaterialsAppState.selectedMaterial.totalValue
                          ? `${CURRENCY_SYMBOL}${academicsMaterialsAppState.selectedMaterial.totalValue.toFixed(2)}`
                          : translateText('academics.materials.nA')}
                      </Typography>
                    </Grid>
                    {academicsMaterialsAppState.selectedMaterial
                      .description && (
                        <Grid item xs={12} data-testid="metadata-description">
                          <Typography variant="caption" color="textSecondary">
                            {translateText('academics.materials.description')}
                          </Typography>
                          <Typography variant="body1" fontWeight="medium">
                            {
                              academicsMaterialsAppState.selectedMaterial
                                .description
                            }
                          </Typography>
                        </Grid>
                      )}
                    {academicsMaterialsAppState.selectedMaterial.notes && (
                      <Grid item xs={12} data-testid="metadata-notes">
                        <Typography variant="caption" color="textSecondary">
                          {translateText('academics.materials.notes')}
                        </Typography>
                        <Typography variant="body1" fontWeight="medium">
                          {academicsMaterialsAppState.selectedMaterial.notes}
                        </Typography>
                      </Grid>
                    )}
                  </Grid>
                </CardContent>
              </Card>
            </Grid>
          </>
        )}

      {viewType === EMaterialView.INDIVIDUAL_DETAILS &&
        academicsMaterialsAppState.selectedEachMaterial && (
          <Grid
            item
            xs={12}
            sx={{ mb: 2 }}
            data-testid="individual-material-details-container"
          >
            <Card variant="outlined">
              <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                <Typography
                  variant="h6"
                  fontWeight="bold"
                  sx={{ mb: 1.5 }}
                  data-testid="individual-material-title"
                >
                  {translateText(
                    'academics.materials.individualMaterialDetails'
                  )}
                </Typography>
                <Divider sx={{ mb: 1.5 }} />
                <Grid container spacing={2}>
                  <Grid
                    item
                    xs={12}
                    sm={6}
                    md={3}
                    data-testid="individual-material-name"
                  >
                    <Typography variant="caption" color="textSecondary">
                      {translateText('academics.materials.name')}
                    </Typography>
                    <Typography variant="body1" fontWeight="medium">
                      {academicsMaterialsAppState.selectedEachMaterial.name ||
                        translateText('academics.materials.nA')}
                    </Typography>
                  </Grid>
                  <Grid
                    item
                    xs={12}
                    sm={6}
                    md={3}
                    data-testid="individual-material-type"
                  >
                    <Typography variant="caption" color="textSecondary">
                      {translateText('academics.materials.columns.type')}
                    </Typography>
                    <Typography variant="body1" fontWeight="medium">
                      {academicsMaterialsAppState.selectedEachMaterial.type ||
                        translateText('academics.materials.nA')}
                    </Typography>
                  </Grid>
                  <Grid
                    item
                    xs={12}
                    sm={6}
                    md={3}
                    data-testid="individual-material-category"
                  >
                    <Typography variant="caption" color="textSecondary">
                      {translateText('academics.materials.columns.category')}
                    </Typography>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.5,
                        mt: 0.5,
                      }}
                    >
                      {getMaterialIcon(
                        academicsMaterialsAppState.selectedEachMaterial.category
                      )}
                      <Typography variant="body1" fontWeight="medium">
                        {academicsMaterialsAppState.selectedEachMaterial
                          .category || translateText('academics.materials.nA')}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid
                    item
                    xs={12}
                    sm={6}
                    md={3}
                    data-testid="individual-material-status"
                  >
                    <Typography variant="caption" color="textSecondary">
                      {translateText('academics.materials.columns.status')}
                    </Typography>
                    <Box sx={{ mt: 0.5 }}>
                      <Chip
                        label={
                          academicsMaterialsAppState.selectedEachMaterial.status
                        }
                        color={
                          academicsMaterialsAppState.selectedEachMaterial
                            .status === MaterialStatus.AVAILABLE
                            ? 'success'
                            : academicsMaterialsAppState.selectedEachMaterial
                              .status === MaterialStatus.IN_USE
                              ? 'info'
                              : academicsMaterialsAppState.selectedEachMaterial
                                .status === MaterialStatus.MAINTENANCE
                                ? 'warning'
                                : academicsMaterialsAppState
                                  .selectedEachMaterial.status ===
                                  MaterialStatus.RETIRED
                                  ? 'default'
                                  : 'error'
                        }
                        size="small"
                      />
                    </Box>
                  </Grid>
                  <Grid
                    item
                    xs={12}
                    sm={6}
                    md={3}
                    data-testid="individual-material-condition"
                  >
                    <Typography variant="caption" color="textSecondary">
                      {translateText('academics.materials.columns.condition')}
                    </Typography>
                    <Box sx={{ mt: 0.5 }}>
                      <Chip
                        label={
                          academicsMaterialsAppState.selectedEachMaterial
                            .condition
                        }
                        color={
                          academicsMaterialsAppState.selectedEachMaterial
                            .condition === MaterialCondition.NEW
                            ? 'success'
                            : academicsMaterialsAppState.selectedEachMaterial
                              .condition === MaterialCondition.LIKE_NEW
                              ? 'info'
                              : academicsMaterialsAppState.selectedEachMaterial
                                .condition === MaterialCondition.GOOD
                                ? 'primary'
                                : academicsMaterialsAppState
                                  .selectedEachMaterial.condition ===
                                  MaterialCondition.FAIR
                                  ? 'warning'
                                  : 'error'
                        }
                        size="small"
                      />
                    </Box>
                  </Grid>
                  <Grid
                    item
                    xs={12}
                    sm={6}
                    md={3}
                    data-testid="individual-material-quantity"
                  >
                    <Typography variant="caption" color="textSecondary">
                      {translateText('academics.materials.columns.quantity')}
                    </Typography>
                    <Typography variant="body1" fontWeight="medium">
                      {academicsMaterialsAppState.selectedEachMaterial
                        .quantity ?? translateText('academics.materials.nA')}
                    </Typography>
                  </Grid>
                  <Grid
                    item
                    xs={12}
                    sm={6}
                    md={3}
                    data-testid="individual-material-minimum-quantity"
                  >
                    <Typography variant="caption" color="textSecondary">
                      {translateText('academics.materials.minimumQuantity')}
                    </Typography>
                    <Typography variant="body1" fontWeight="medium">
                      {academicsMaterialsAppState.selectedEachMaterial
                        .minimumQuantity ??
                        translateText('academics.materials.nA')}
                    </Typography>
                  </Grid>
                  <Grid
                    item
                    xs={12}
                    sm={6}
                    md={3}
                    data-testid="individual-material-cost"
                  >
                    <Typography variant="caption" color="textSecondary">
                      {translateText('academics.materials.columns.cost')}
                    </Typography>
                    <Typography variant="body1" fontWeight="medium">
                      {academicsMaterialsAppState.selectedEachMaterial.cost
                        ? `${CURRENCY_SYMBOL}${academicsMaterialsAppState.selectedEachMaterial.cost.toFixed(2)}`
                        : translateText('academics.materials.nA')}
                    </Typography>
                  </Grid>
                  <Grid
                    item
                    xs={12}
                    sm={6}
                    md={3}
                    data-testid="individual-material-supplier"
                  >
                    <Typography variant="caption" color="textSecondary">
                      {translateText('academics.materials.columns.supplier')}
                    </Typography>
                    <Typography variant="body1" fontWeight="medium">
                      {academicsMaterialsAppState.selectedEachMaterial
                        .supplier || translateText('academics.materials.nA')}
                    </Typography>
                  </Grid>
                  <Grid
                    item
                    xs={12}
                    sm={6}
                    md={3}
                    data-testid="individual-material-location"
                  >
                    <Typography variant="caption" color="textSecondary">
                      {translateText('academics.materials.columns.location')}
                    </Typography>
                    <Typography variant="body1" fontWeight="medium">
                      {academicsMaterialsAppState.selectedEachMaterial
                        .location || translateText('academics.materials.nA')}
                    </Typography>
                  </Grid>
                  <Grid
                    item
                    xs={12}
                    sm={6}
                    md={3}
                    data-testid="individual-material-purchase-date"
                  >
                    <Typography variant="caption" color="textSecondary">
                      {translateText('academics.materials.purchaseDate')}
                    </Typography>
                    <Typography variant="body1" fontWeight="medium">
                      {academicsMaterialsAppState.selectedEachMaterial
                        .purchaseDate
                        ? new Date(
                          academicsMaterialsAppState.selectedEachMaterial.purchaseDate
                        ).toLocaleDateString()
                        : translateText('academics.materials.nA')}
                    </Typography>
                  </Grid>
                  {academicsMaterialsAppState.selectedEachMaterial
                    .lastMaintenanceDate && (
                      <Grid
                        item
                        xs={12}
                        sm={6}
                        md={3}
                        data-testid="individual-material-last-maintenance-date"
                      >
                        <Typography variant="caption" color="textSecondary">
                          {translateText(
                            'academics.materials.lastMaintenanceDate'
                          )}
                        </Typography>
                        <Typography variant="body1" fontWeight="medium">
                          {new Date(
                            academicsMaterialsAppState.selectedEachMaterial.lastMaintenanceDate
                          ).toLocaleDateString()}
                        </Typography>
                      </Grid>
                    )}
                  {academicsMaterialsAppState.selectedEachMaterial.tags &&
                    academicsMaterialsAppState.selectedEachMaterial.tags
                      .length > 0 && (
                      <Grid item xs={12} data-testid="individual-material-tags">
                        <Typography variant="caption" color="textSecondary">
                          {translateText('academics.materials.tags')}
                        </Typography>
                        <Box
                          sx={{
                            display: 'flex',
                            gap: 1,
                            flexWrap: 'wrap',
                            mt: 0.5,
                          }}
                        >
                          {academicsMaterialsAppState.selectedEachMaterial.tags.map(
                            (tag, index) => (
                              <Chip
                                key={index}
                                label={tag}
                                size="small"
                                variant="outlined"
                              />
                            )
                          )}
                        </Box>
                      </Grid>
                    )}
                  {academicsMaterialsAppState.selectedEachMaterial
                    .description && (
                      <Grid
                        item
                        xs={12}
                        data-testid="individual-material-description"
                      >
                        <Typography variant="caption" color="textSecondary">
                          {translateText('academics.materials.description')}
                        </Typography>
                        <Typography variant="body1" fontWeight="medium">
                          {
                            academicsMaterialsAppState.selectedEachMaterial
                              .description
                          }
                        </Typography>
                      </Grid>
                    )}
                  {academicsMaterialsAppState.selectedEachMaterial.type ===
                    'book' &&
                    'isbn' in
                    academicsMaterialsAppState.selectedEachMaterial && (
                      <>
                        <Grid
                          item
                          xs={12}
                          sm={6}
                          md={3}
                          data-testid="individual-material-isbn"
                        >
                          <Typography variant="caption" color="textSecondary">
                            ISBN
                          </Typography>
                          <Typography variant="body1" fontWeight="medium">
                            {academicsMaterialsAppState.selectedEachMaterial
                              .isbn || translateText('academics.materials.nA')}
                          </Typography>
                        </Grid>
                        <Grid
                          item
                          xs={12}
                          sm={6}
                          md={3}
                          data-testid="individual-material-author"
                        >
                          <Typography variant="caption" color="textSecondary">
                            {translateText('academics.library.author')}
                          </Typography>
                          <Typography variant="body1" fontWeight="medium">
                            {academicsMaterialsAppState.selectedEachMaterial
                              .author ||
                              translateText('academics.materials.nA')}
                          </Typography>
                        </Grid>
                        <Grid
                          item
                          xs={12}
                          sm={6}
                          md={3}
                          data-testid="individual-material-publisher"
                        >
                          <Typography variant="caption" color="textSecondary">
                            Publisher
                          </Typography>
                          <Typography variant="body1" fontWeight="medium">
                            {academicsMaterialsAppState.selectedEachMaterial
                              .publisher ||
                              translateText('academics.materials.nA')}
                          </Typography>
                        </Grid>
                        <Grid
                          item
                          xs={12}
                          sm={6}
                          md={3}
                          data-testid="individual-material-edition"
                        >
                          <Typography variant="caption" color="textSecondary">
                            Edition
                          </Typography>
                          <Typography variant="body1" fontWeight="medium">
                            {academicsMaterialsAppState.selectedEachMaterial
                              .edition ||
                              translateText('academics.materials.nA')}
                          </Typography>
                        </Grid>
                      </>
                    )}
                  {academicsMaterialsAppState.selectedEachMaterial.type ===
                    'lab_equipment' &&
                    'equipmentType' in
                    academicsMaterialsAppState.selectedEachMaterial && (
                      <>
                        <Grid
                          item
                          xs={12}
                          sm={6}
                          md={3}
                          data-testid="individual-material-equipment-type"
                        >
                          <Typography variant="caption" color="textSecondary">
                            Equipment Type
                          </Typography>
                          <Typography variant="body1" fontWeight="medium">
                            {academicsMaterialsAppState.selectedEachMaterial
                              .equipmentType ||
                              translateText('academics.materials.nA')}
                          </Typography>
                        </Grid>
                        <Grid
                          item
                          xs={12}
                          sm={6}
                          md={3}
                          data-testid="individual-material-model-number"
                        >
                          <Typography variant="caption" color="textSecondary">
                            Model Number
                          </Typography>
                          <Typography variant="body1" fontWeight="medium">
                            {academicsMaterialsAppState.selectedEachMaterial
                              .modelNumber ||
                              translateText('academics.materials.nA')}
                          </Typography>
                        </Grid>
                        <Grid
                          item
                          xs={12}
                          sm={6}
                          md={3}
                          data-testid="individual-material-manufacturer"
                        >
                          <Typography variant="caption" color="textSecondary">
                            Manufacturer
                          </Typography>
                          <Typography variant="body1" fontWeight="medium">
                            {academicsMaterialsAppState.selectedEachMaterial
                              .manufacturer ||
                              translateText('academics.materials.nA')}
                          </Typography>
                        </Grid>
                        <Grid
                          item
                          xs={12}
                          sm={6}
                          md={3}
                          data-testid="individual-material-serial-number"
                        >
                          <Typography variant="caption" color="textSecondary">
                            Serial Number
                          </Typography>
                          <Typography variant="body1" fontWeight="medium">
                            {academicsMaterialsAppState.selectedEachMaterial
                              .serialNumber ||
                              translateText('academics.materials.nA')}
                          </Typography>
                        </Grid>
                      </>
                    )}
                  {academicsMaterialsAppState.selectedEachMaterial.type ===
                    'digital_resource' &&
                    'resourceType' in
                    academicsMaterialsAppState.selectedEachMaterial && (
                      <>
                        <Grid
                          item
                          xs={12}
                          sm={6}
                          md={3}
                          data-testid="individual-material-resource-type"
                        >
                          <Typography variant="caption" color="textSecondary">
                            Resource Type
                          </Typography>
                          <Typography variant="body1" fontWeight="medium">
                            {academicsMaterialsAppState.selectedEachMaterial
                              .resourceType ||
                              translateText('academics.materials.nA')}
                          </Typography>
                        </Grid>
                        <Grid
                          item
                          xs={12}
                          sm={6}
                          md={3}
                          data-testid="individual-material-format"
                        >
                          <Typography variant="caption" color="textSecondary">
                            Format
                          </Typography>
                          <Typography variant="body1" fontWeight="medium">
                            {academicsMaterialsAppState.selectedEachMaterial
                              .format ||
                              translateText('academics.materials.nA')}
                          </Typography>
                        </Grid>
                        <Grid
                          item
                          xs={12}
                          sm={6}
                          md={3}
                          data-testid="individual-material-url"
                        >
                          <Typography variant="caption" color="textSecondary">
                            URL
                          </Typography>
                          <Typography variant="body1" fontWeight="medium">
                            {academicsMaterialsAppState.selectedEachMaterial
                              .url ? (
                              <a
                                href={
                                  academicsMaterialsAppState
                                    .selectedEachMaterial.url
                                }
                                target="_blank"
                                rel="noopener noreferrer"
                              >
                                {translateText(
                                  'academics.materials.viewResource'
                                )}
                              </a>
                            ) : (
                              translateText('academics.materials.nA')
                            )}
                          </Typography>
                        </Grid>
                      </>
                    )}
                  {academicsMaterialsAppState.selectedEachMaterial.type ===
                    'stationery' &&
                    'stationeryType' in
                    academicsMaterialsAppState.selectedEachMaterial && (
                      <>
                        <Grid
                          item
                          xs={12}
                          sm={6}
                          md={3}
                          data-testid="individual-material-stationery-type"
                        >
                          <Typography variant="caption" color="textSecondary">
                            Stationery Type
                          </Typography>
                          <Typography variant="body1" fontWeight="medium">
                            {academicsMaterialsAppState.selectedEachMaterial
                              .stationeryType ||
                              translateText('academics.materials.nA')}
                          </Typography>
                        </Grid>
                        <Grid
                          item
                          xs={12}
                          sm={6}
                          md={3}
                          data-testid="individual-material-brand"
                        >
                          <Typography variant="caption" color="textSecondary">
                            Brand
                          </Typography>
                          <Typography variant="body1" fontWeight="medium">
                            {academicsMaterialsAppState.selectedEachMaterial
                              .brand || translateText('academics.materials.nA')}
                          </Typography>
                        </Grid>
                      </>
                    )}
                </Grid>
              </CardContent>
            </Card>
          </Grid>
        )}

      {viewType !== EMaterialView.INDIVIDUAL_DETAILS && (
        <>
          <Grid
            item
            xs={12}
            data-testid="academics-materials-grid-container"
            sx={{
              height: 'calc(100vh - 250px)',
              overflow: 'auto',
            }}
          >
            <PaginatedGrid
              data-testid="academics-materials-grid"
              rows={
                viewType === EMaterialView.MATERIALS
                  ? filteredMaterials
                  : academicsMaterialsAppState.selectedMaterial
                    ? academicsMaterialsAppState.selectedMaterial.materials.map(
                      (material) => ({
                        id: material.id,
                        name: material.name,
                        type: material.type,
                        status: material.status,
                        quantity: material.quantity,
                        cost: material.cost,
                        supplier: material.supplier,
                        purchaseDate: material.purchaseDate,
                      })
                    )
                    : []
              }
              columns={
                viewType === EMaterialView.MATERIALS
                  ? materialsGridColumns
                  : materialDetailsGridColumns
              }
              loading={isLoading}
              onRowClick={
                viewType === EMaterialView.MATERIALS ||
                  viewType === EMaterialView.DETAILS
                  ? handleRowClick
                  : undefined
              }
              getRowId={(row) => {
                if (!row || !row.id) {
                  console.warn('Invalid row data:', row);
                  return '';
                }
                return row.id;
              }}
              hideColumnsOnMobile={
                viewType === EMaterialView.MATERIALS
                  ? ['totalCosts']
                  : ['supplier', 'purchaseDate']
              }
            />
          </Grid>
          {viewType === EMaterialView.DETAILS && (
            <Grid
              item
              xs={12}
              sx={{
                display: 'flex',
                justifyContent: 'flex-end',
                my: { xs: 2, sm: 2 },
              }}
            >
              <AddButton
                label={translateText('academics.materials.addItem')}
                onClickHandler={handleAddNewMaterialToGroup}
              />
            </Grid>
          )}
        </>
      )}
    </Grid>
  );
};

export default AcademicsMaterialsGrid;
